import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DomSanitizer, SafeHtml, SafeResourceUrl } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { faCartArrowDown, faCropAlt, faDownload, faExpandArrowsAlt, faPen, faPlus, faTrashAlt, faUnlink } from "@fortawesome/free-solid-svg-icons";
import { NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { SwalComponent } from "@sweetalert2/ngx-sweetalert2";
import { UserService } from "app/core/services/admin/user/user.service";
import { DataModelService } from "app/core/services/dam/datamodel.service";
import { MediaService } from "app/core/services/dam/media.service";
import { HistoryService } from "app/core/services/global/history/history.service";
import { SwalModalService } from "app/core/services/global/modal/modal.service";
import { ToastService } from "app/core/services/global/toast/toast.service";
import { TagService } from "app/core/services/tag/tag.service";
import { UploadService } from "app/core/services/upload/upload.service";
import { ProjectExportService } from "app/core/services/project/export/export.service";
import { CropperComponent } from "app/shared/components/cropper/cropper.component";
import { MediaType } from "app/shared/helpers/media-type.helper";
import { ThumbButtons } from "app/shared/helpers/thumb-buttons";
import { MaestroTags } from "app/shared/models";
import { ACL } from "app/shared/models/acl";
import { Thumb } from "app/shared/models/thumb";
import { environment } from "src/environments";
import { PreviewService } from "../../../../core/services/thumbnails/preview.service";
import { ThumbnailsService } from "../../../../core/services/thumbnails/thumbnails.service";

interface GetThumbParams {
    binImage: any;
}

@Component({
    selector: "app-preview",
    templateUrl: "./preview.component.html",
    styleUrls: ["./preview.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [ProjectExportService],
})
export class PreviewComponent implements OnInit {
    options: NgbModalOptions = {};
    @Input() buttons: ThumbButtons;
    workflowData = [];
    @Input() media: Thumb;
    active = 1;
    @Output() public tagRemoved = new EventEmitter<void>();

    @Input() tags: MaestroTags = [];
    filteredTags = [];
    formatDL : string = "HD";

    public faDownload = faDownload;
    public faExpandArrowsAlt = faExpandArrowsAlt;
    public faTrashAlt = faTrashAlt;
    public faUnlink = faUnlink;
    readonly faBucket = faCartArrowDown;
    readonly faPlus = faPlus;
    readonly faCrop = faCropAlt;
    readonly faPen = faPen;

    editingName = false;

    private slideIndex = 1;

    metadata: SafeHtml;
    fields: SafeHtml;
    formAddTag: FormGroup;

    @ViewChild("content", {static: true}) private content: string;
    @ViewChild("datamodel") public dataModelModal: SwalComponent;
    @ViewChild("addTagModal") public addTagModal: SwalComponent;

    dataModelForm: FormGroup;
    @Input() dataModels = [];

    @Output() public next = new EventEmitter<boolean>();
    @Output() public previous = new EventEmitter<boolean>();
    @Output() public unlinked = new EventEmitter<number>();
    @Output() public addCart = new EventEmitter<Thumb>();
    @Output() public deleted = new EventEmitter<number>();

    @ViewChild("cropper") cropper: CropperComponent;

    previewData: any;

    acl: ACL;

    damUrl: string;

    typeFile = [];
    extension : string = "";
    type: string = "";

    // Téléchargement par format/résolution (point 2)
    formats: any[] = [];
    selectedFormatName: string = "";
    resolutions: any[] = [];
    selectedResolution: { width: number; height: number; dpi: number } = null;

    get canAddTag(): boolean {
        return this.acl.SYSPAD_DAM_TAG_UPDATE && window.location.href.includes("dam");
    }

    /** Whether the current user can rename the media (DAM update right). */
    get canRename(): boolean {
        return !!this.acl?.MAESTRO_DAM_MEDIA_UPDATE;
    }

    /** Nom du média sans son extension (pour pré-remplir la saisie de renommage). */
    get nameBase(): string {
        const name = this.media?.name ?? "";
        const ext = this.media?.extension;
        if (ext && name.toLowerCase().endsWith("." + ext.toLowerCase())) {
            return name.slice(0, -(ext.length + 1));
        }
        const idx = name.lastIndexOf(".");
        return idx > 0 ? name.slice(0, idx) : name;
    }

    /**
     * Renomme le média depuis la preview (l'extension d'origine est conservée).
     *
     * @param value nouveau nom de base saisi
     */
    saveName(value: string): void {
        const base = (value || "").trim();
        if (!base) {
            this.editingName = false;
            this._cdr.markForCheck();
            return;
        }
        const name = this.media.name || "";
        let ext = "";
        if (this.media.extension && name.toLowerCase().endsWith("." + this.media.extension.toLowerCase())) {
            ext = "." + this.media.extension;
        } else {
            const idx = name.lastIndexOf(".");
            if (idx > 0) {
                ext = name.slice(idx);
            }
        }
        const newName = base + ext;

        this._mediaService.renameMedia(this.media.id, newName).subscribe((res: any) => {
            this.media.name = res?.data?.name ? res.data.name : newName;
            this.editingName = false;
            this._toaster.show({ message: this._translateService.instant("dam.renamed"), type: "success" });
            this._cdr.markForCheck();
        });
    }

    constructor(
        private thumbnailsService: ThumbnailsService,
        private router: Router,
        private route: ActivatedRoute,
        private modalService: NgbModal,
        private service: PreviewService,
        private sanitizer: DomSanitizer,
        private _translateService: TranslateService,
        private _swalModal: SwalModalService,
        private _uploadService: UploadService,
        private _damService: DataModelService,
        private _fb: FormBuilder,
        private _mediaService: MediaService,
        private _historyService: HistoryService,
        private _tagService: TagService,
        private _userService: UserService,
        private _exportService: ProjectExportService,
        private _toaster: ToastService,
        private _cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        this.acl = this._userService.getUserAclFromToken();

        if (!this.acl.SYSPAD_PIM_MEDIA_READ) {
            this.buttons.preview = false;
        }
        if (!this.acl.SYSPAD_PIM_MEDIA_DELETE) {
            this.buttons.unlink = false;
        }
        this.options = {windowClass: "modal-dam dark", size: "xl" as "lg"};

        this.dataModelForm = this._fb.group({
            dm: [[], Validators.required],
        });

        this.formAddTag = this._fb.group({
            tag: [[], Validators.required],
        });

        this.damUrl = environment.damUrl;

        // Formats de crop + leurs résolutions disponibles (point 2).
        // Un format sans résolution (ex : CDF, HTML) n'apporte rien au téléchargement : on le masque.
        this.service.getFormats().subscribe((r: any) => {
            this.formats = (r && r.data ? r.data : []).filter((f) => f.resolutions && f.resolutions.length);
        });
    }

    /**
     * Met à jour les résolutions disponibles selon le format choisi et
     * présélectionne la première (= « Standard »). Format vide / sans
     * résolution → téléchargement HD/BD classique (aucune résolution envoyée).
     */
    onFormatChange(name: string): void {
        this.selectedFormatName = name;
        const format = this.formats.find((f) => f.name === name);
        this.resolutions = format && format.resolutions ? format.resolutions : [];
        this.selectedResolution = this.resolutions.length ? this.resolutions[0] : null;
        // Un redimensionnement se calcule depuis l'original : la qualité HD/BD ne s'applique
        // qu'au téléchargement « Taille d'origine » (le select qualité est masqué sinon).
        if (name) {
            this.formatDL = "HD";
        }
    }

    /**
     * Get preview information & open the preview modal
     */
    open() {
        this.active = 1;
        this.metadata = null;
        this.fields = null;
        this.selectedFormatName = "";
        this.resolutions = [];
        this.selectedResolution = null;
        this.formatDL = "HD";
        let keys = Object.keys(MediaType);
        keys.forEach((key) => {
            if(MediaType[key].includes(this.media.extension)){
                this.typeFile = MediaType[key];
                if(key == "image"){
                    // jpg/jpeg et tif/tiff sont des alias du même format : on ne propose
                    // qu'une variante au téléchargement (jpg, tif) et on normalise
                    // l'extension par défaut pour qu'elle corresponde à une option du select.
                    const downloadAlias = { jpeg: "jpg", tiff: "tif" };
                    this.extension = downloadAlias[this.media.extension] || this.media.extension;
                    const notDuplicateFormat = (ext) => ext != "jpeg" && ext != "tiff";
                    if(this.media.extension == "svg"){
                        this.typeFile = MediaType[key].filter((ext) => ext !== ext.toUpperCase() && notDuplicateFormat(ext));
                    } else {
                        this.typeFile = MediaType[key].filter((ext) => ext != "svg" && ext !== ext.toUpperCase() && notDuplicateFormat(ext));
                    }
                }
                this.type = key;
            }
        });


        this.service.getPreview(this.media.id).subscribe((data) => {
            this.previewData = data.data;
            this.setMetadata();
            this.setFields();
            this.modalService.open(this.content, this.options);
            this.dataModelForm.patchValue({
                dm: [this.media.model],
            });
            this.previewData.story = this.previewData.story.reverse();
            setTimeout(() => this._showSlide(1), 2000);
            setTimeout(() => this.prepareFullScreen(), 2000);
        });
    }

    /**
     * Update the preview modal
     */
    update(): void {
        this.metadata = null;
        this.fields = null;
        this.service.getPreview(this.media.id).subscribe((data) => {
            this.previewData = data.data;
            this.setMetadata();
            this.setFields();
            this.modalService.dismissAll();
            this.modalService.open(this.content, this.options);
            this.dataModelForm.patchValue({
                dm: [this.media.model],
            });
            this.previewData.story = this.previewData.story.reverse();
            setTimeout(() => this._showSlide(1), 2000);
            setTimeout(() => this.prepareFullScreen(), 2000);
        });
    }

    previousMedia(): void {
        this.active = 1;
        this.previous.emit(true);
    }

    nextMedia(): void {
        this.active = 1;
        this.next.emit(true);
    }

    /**
     * Delete a media
     *
     * @param id
     */
    deleteMedia(id: number): void {
        this._swalModal.deleteWithContent("modal.mediadam.delete").then((result) => {
            if (result.value) {
                this.modalService.dismissAll();
                this.deleted.emit(id);
            }
        });
    }

    /**
     * Unlink a media
     *
     * @param id
     */
    unlinkMedia(id: number): void {
        this.unlinked.emit(id);
        this.modalService.dismissAll();
    }

    /**
     * Download a media
     */
    download(pathHD = ""): void {
        
        this._uploadService.getMediaTypes().subscribe((data) => {
            let header = "";
            if(this.extension){
                header = data.find((d) => d.extension === this.extension).header;
            } else {
                header = data.find((d) => d.extension === this.media.extension).header;
            }
            if (header) {
                this.service.getDownloadFile(this.media.id, this.formatDL, this.extension, this.selectedResolution).subscribe((data) => {
                    // Blob (object URL) au lieu d'une data: URI : Safari iOS plafonne la taille
                    // des data: URI et abandonne silencieusement le téléchargement HD (trop lourd).
                    const linkSource = this._exportService.createBlobUrl(data.data.file, header);

                    this.downloadFile(linkSource, data.data.filename);

                    this._historyService
                        .setHistory({
                            scenario: "download",
                            objectId: this.media.id,
                            userId: this._userService.getUserIdFromToken(),
                            module: "dam",
                            className: "Media",
                            value: '{"name": "mediaName"}',
                            version: 0,
                        })
                        .subscribe();
                });
            }
        });
    }

    /**
     * Get media thumb
     */
    public getThumb({binImage}: GetThumbParams) {
        let img =
            "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";
        if (binImage) {
            img = binImage;
        }

        if (this.previewData.file) {
            img = this.previewData.file;
        } else {
            img = this.media.thumb;
        }

        const src = `data:image/jpeg;base64,${img}`;

        return this.sanitizer.bypassSecurityTrustUrl(src);
    }

    public getChildThumb(thumb: string): SafeResourceUrl {
        const src = `data:image/jpeg;base64,${thumb}`;
        return this.sanitizer.bypassSecurityTrustUrl(src);
    }

    /**
     * Calculate the media width & height using the window size
     */
    getImageStyle(): { width: string; height: string } {
        const divHeight = $("#image-container").innerHeight();
        const divWidth = $("#image-container").innerWidth();

        const imageHeight = ($("#image")[0] as HTMLImageElement).naturalHeight;
        const imageWidth = ($("#image")[0] as HTMLImageElement).naturalWidth;

        return this.resizeImage(imageWidth, imageHeight, divWidth, divHeight);
    }

    /**
     * Resize an image
     *
     * @param imgWidth
     * @param imgHeight
     * @param containerWidth
     * @param containerHeight
     * @returns
     */
    resizeImage(imgWidth: number, imgHeight: number, containerWidth: number, containerHeight: number): { width: string; height: string } {
        const coord = new Array(2);
        const ratio = imgHeight / imgWidth;
        if (imgWidth < containerWidth && imgHeight < containerHeight) {
            while (imgWidth < containerWidth && imgHeight < containerHeight) {
                imgWidth++;
                imgHeight = Math.round(imgWidth * ratio);
            }
        } else {
            while (imgWidth > containerWidth || imgHeight > containerHeight) {
                imgWidth--;
                imgHeight = Math.round(imgWidth * ratio);
            }
        }
        coord[0] = imgWidth;
        coord[1] = imgHeight;
        return {width: coord[0] + "px", height: coord[1] + "px"};
    }

    /**
     * Set metadata to the DOM
     */
    setMetadata(): void {
        if (this.media.isExpired) {
            this.previewData.metadata.metadata['isExpired'] = this.media.isExpired;
        }
        if (this.previewData.metadata.metadata) {

            let html = "<table class='table border-bottom mb-0 col-12'><tbody>";
            Object.keys(this.previewData.metadata.metadata).forEach((k) => {
                html += `
                    <tr>
                        <td class='font-weight-bold'>${this._translateService.instant("dam.metadata." + k)}</td>
                        <td>${this.previewData.metadata.metadata[k]}</td>
                    </tr>`;
            });
            html += "</tbody></table>";
            this.metadata = this.sanitizer.bypassSecurityTrustHtml(html);
        }
    }

    /**
     * Set Maestro fields to the DOM
     */
    setFields(): void {
        if (this.previewData.metadata.fields) {
            let html = "<table class='table border-bottom mb-0 col-12'><tbody>";
            Object.keys(this.previewData.metadata.fields).forEach((k) => {
                html += `
                    <tr>
                        <td class='font-weight-bold'>${k}</td>
                        <td>${this.previewData.metadata.fields[k]}</td>
                    </tr>`;
            });
            html += "</tbody></table>";
            this.fields = this.sanitizer.bypassSecurityTrustHtml(html);
        }
    }

    /**
     * Prepare full screen
     */
    prepareFullScreen(): void {
        const showFullScreen = this._showFullScreen;
        $("img[data-enlargeable]")
            .addClass("img-enlargeable")
            .on("click", function () {
                showFullScreen($(this).attr("src"));
            });
    }

    /**
     * Open the current preview image in full screen (zoom button)
     */
    openFullScreen(): void {
        const src = $(".modal-dam img#image").attr("src");
        if (src) {
            this._showFullScreen(src);
        }
    }

    private _showFullScreen(src: string): void {
        var modal;

        function removeModal() {
            modal.remove();
            $("body").off("keyup.modal-close");
        }

        modal = $("<div>")
            .css({
                background: "RGBA(0,0,0,.5) url(" + src + ") no-repeat center",
                backgroundSize: "contain",
                width: "100%",
                height: "100%",
                position: "fixed",
                zIndex: "10000",
                top: "0",
                left: "0",
                cursor: "zoom-out",
            })
            .on("click", function () {
                removeModal();
            })
            .appendTo("body");
        //handling ESC
        $("body").on("keyup.modal-close", function (e) {
            if (e.key === "Escape") {
                removeModal();
            }
        });
    }

    /**
     * Redirect to the model creation
     *
     * @param id
     */
    createMedia(id: number): void {
        this.dataModelModal.fire().then((result) => {
            if (result.value) {
                if (this.dataModelForm.value.dm.length > 0) {
                    this._damService.setModel(id, this.dataModelForm.value.dm[0].id).subscribe(() => {
                        this.media.model = this.dataModelForm.value.dm[0];
                    });
                } else {
                    this._damService.setModel(id, null).subscribe(() => {
                        this.media.model = null;
                    });
                }
            }
        });
    }

    /**
     * Open the crop modal
     *
     * @param mediaId
     */
    cropMedia(mediaId: number): void {
        this.cropper.openCrop(mediaId);
    }

    /**
     * Create a cropped image
     *
     * @param $event
     */
    croppedMedia($event): void {
        let flipH = $event.transform.flipH ? $event.transform.flipH : false;
        let flipV = $event.transform.flipV ? $event.transform.flipV : false;
        let crop = {
            x: $event.croppedImage.cropperPosition.x1,
            y: $event.croppedImage.cropperPosition.y1,
            height: $event.croppedImage.height,
            width: $event.croppedImage.width,
            rotate: $event.rotation,
            flipH: flipH,
            flipV: flipV,
        };
        this._mediaService.createCroppedImage(this.media.id, crop, $event.format).subscribe(() => {
            this.update();
        });
    }

    nextSlide(direction: number): void {
        this._showSlide((this.slideIndex += direction));
    }

    setCurrentSlide(position: number): void {
        this._showSlide((this.slideIndex = position));
    }

    private _showSlide(position: number): void {
        if (this.previewData.children.length === 0) {
            return;
        }
        let i = 0;
        const slides = document.getElementsByClassName("mySlides") as HTMLCollectionOf<HTMLElement>;
        const dots = document.getElementsByClassName("demo") as HTMLCollectionOf<HTMLImageElement>;
        const captionText = document.getElementById("caption");
        if (position > slides.length) {
            this.slideIndex = 1;
        }
        if (position < 1) {
            this.slideIndex = slides.length;
        }
        for (i = 0; i < slides.length; i++) {
            slides[i].style.display = "none";
        }
        for (i = 0; i < dots.length; i++) {
            dots[i].className = dots[i].className.replace(" active", "");
        }

        slides[this.slideIndex - 1].style.display = "block";
        dots[this.slideIndex - 1].className += " active";
        captionText.innerHTML = dots[this.slideIndex - 1].alt;
    }

    /**
     * Open tag modal
     */
    openTagModal(media: Thumb): void {
        this.addTagModal.fire().then((result) => {
            if (result.value) {
                if (this.formAddTag.value.tag[0]) {
                    this._tagService
                        .addTag(
                            media.id,
                            this.formAddTag.value.tag.map((t) => t.id)
                        )
                        .subscribe((response: any) => {
                            this.media.tags = response.data;
                        });
                }
            }
        });
        this.addTagModal.update({
            title: this._translateService.instant("modal.addTag", {key: media.name}),
        });
        this.filteredTags = this.tags.filter((t) => !media.tags.map((mt) => mt.id).includes(t.id));
    }

    /**
     * Remove a tag
     */
    removeTag(media: Thumb, tagId: number): void {
        this._tagService.removeTag(media.id, {id: tagId}).subscribe((response: any) => {
            this.media.tags = response.data;
            this.tagRemoved.emit();
        });
    }

    /**
     * Check if valid url
     */
    validURL(str) {
        let pattern = new RegExp(
            "^(https?:\\/\\/)?" + // protocol
            "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
            "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
            "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
            "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
            "(\\#[-a-z\\d_]*)?$",
            "i"
        ); // fragment locator
        return !!pattern.test(str);
    }

    /**
     * Download file
     */
    async downloadFile(link, filename) {
        const downloadLink = document.createElement("a");

        downloadLink.href = link;
        downloadLink.download = filename;
        // Pas de target="_blank" : inutile avec un object URL + download, et sur iOS
        // cela peut ouvrir un onglet vide.
        downloadLink.click();

        // Libère l'object URL créé par createBlobUrl
        URL.revokeObjectURL(link);
    }

    changeFormat(event){
        console.log(event);
        
    }
}
